import React, { Fragment, useState, useEffect } from 'react';

const THEME_COLORS = {
  admin: {
    active: 'bg-red-600 text-white shadow-lg shadow-red-200 transform scale-105',
    text: 'text-gray-900',
  },
  employee: {
    active: 'bg-blue-800 text-white shadow-lg shadow-blue-200 transform scale-105',
    text: 'text-gray-900',
  },
};

// Returns the number of page pills to show based on viewport width.
// xs (<640px):  3 pills — current ±1, prevents capsule overflow on phones
// sm/md (<1024px): 5 pills — current ±2, comfortable for tablet
// lg+ (≥1024px): 7 pills — current ±3, full desktop experience
const useMaxVisible = () => {
  const getMax = () => {
    if (typeof window === 'undefined') return 7;
    if (window.innerWidth < 640) return 3;
    if (window.innerWidth < 1024) return 5;
    return 7;
  };
  const [maxVisible, setMaxVisible] = useState(getMax);
  useEffect(() => {
    const handler = () => setMaxVisible(getMax());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return maxVisible;
};

const getVisiblePages = (currentPage, totalPages, maxVisible) => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const half = Math.floor(maxVisible / 2);
  if (currentPage <= half + 1) {
    return Array.from({ length: maxVisible }, (_, i) => i + 1);
  }
  if (currentPage >= totalPages - half) {
    return Array.from({ length: maxVisible }, (_, i) => totalPages - maxVisible + i + 1);
  }
  return Array.from({ length: maxVisible }, (_, i) => currentPage - half + i);
};

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalCount,
  pageSize,
  theme = 'admin',
  noun = 'results',
  variant = 'footer',
  className = '',
}) => {
  const maxVisible = useMaxVisible();

  if (!totalPages || totalPages <= 1) return null;

  const colors = THEME_COLORS[theme] || THEME_COLORS.admin;
  const visiblePages = getVisiblePages(currentPage, totalPages, maxVisible);

  const showSummary = totalCount != null && pageSize != null;
  const rangeStart = showSummary ? (currentPage - 1) * pageSize + 1 : null;
  const rangeEnd = showSummary ? Math.min(currentPage * pageSize, totalCount) : null;

  // footer = table-card bottom strip. standalone = card-list worklist — capsule
  // only, no second bordered region competing with the records above.
  const shellClass = variant === 'standalone'
    ? `py-4 sm:py-5 ${className}`.trim()
    : `px-3 py-4 sm:px-6 sm:py-6 bg-gray-50 border-t border-gray-200 rounded-b-2xl ${className}`.trim();

  return (
    <div className={shellClass}>
      <div className="flex flex-col items-center gap-3 sm:gap-4">
        {showSummary && (
          <div className="text-sm text-gray-600 font-medium">
            Showing <span className={colors.text}>{rangeStart}</span> to{' '}
            <span className={colors.text}>{rangeEnd}</span> of{' '}
            <span className={colors.text}>{totalCount}</span> {noun}
          </div>
        )}

        <div className="flex items-center max-w-full bg-white rounded-full p-1 sm:p-1.5 shadow-sm border border-gray-200">
          {/* Previous */}
          <button
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full transition-[background-color,box-shadow] duration-200 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
              currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : `text-gray-600 hover:text-gray-900 hover:bg-gray-100 ${theme === 'admin' ? 'focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'}`
            }`}
            title="Previous page"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Page numbers */}
          <div className="flex items-center mx-1 sm:mx-2">
            {visiblePages.map((pageNum, index) => {
              const isActive = currentPage === pageNum;
              const isFirst = index === 0;
              const isLast = index === visiblePages.length - 1;

              return (
                <Fragment key={pageNum}>
                  {isFirst && pageNum > 1 && (
                    <div className="px-1.5 sm:px-3 py-2 text-gray-400 text-sm">…</div>
                  )}
                  <button
                    onClick={() => onPageChange(pageNum)}
                    className={`flex items-center justify-center min-w-[32px] sm:min-w-[40px] h-8 sm:h-10 px-1.5 sm:px-3 mx-0.5 sm:mx-1 rounded-full font-medium text-xs sm:text-sm transition-[background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                      isActive
                        ? `${colors.active} ${theme === 'admin' ? 'focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'}`
                        : `text-gray-600 hover:text-gray-900 hover:bg-gray-100 ${theme === 'admin' ? 'focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'}`
                    }`}
                    title={`Page ${pageNum}`}
                  >
                    {pageNum}
                  </button>
                  {isLast && pageNum < totalPages && (
                    <div className="px-1.5 sm:px-3 py-2 text-gray-400 text-sm">…</div>
                  )}
                </Fragment>
              );
            })}
          </div>

          {/* Next */}
          <button
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full transition-[background-color,box-shadow] duration-200 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
              currentPage === totalPages
                ? 'text-gray-400 cursor-not-allowed'
                : `text-gray-600 hover:text-gray-900 hover:bg-gray-100 ${theme === 'admin' ? 'focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'}`
            }`}
            title="Next page"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
